import { FormEvent, useEffect, useMemo, useState } from "react";
import { Loader2, Plus, RefreshCw, Image as ImageIcon, X } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import MediaPicker from "@/components/admin/MediaPicker";
import { supabase } from "@/integrations/supabase/client";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";

interface SocialLinks {
  linkedin?: string;
  instagram?: string;
  facebook?: string;
  website?: string;
}

interface TeamRow {
  id: string;
  name: string;
  slug: string;
  title: string | null;
  bio: string | null;
  short_bio: string | null;
  headshot_url: string | null;
  phone: string | null;
  email: string | null;
  license_number: string | null;
  license_label: string | null;
  specialties: string[];
  languages: string[];
  service_areas: string[];
  social_links: SocialLinks;
  is_featured: boolean;
  is_published: boolean;
  sort_order: number;
}

type PubFilter = "all" | "published" | "draft";

const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").slice(0, 80);

const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const isPhone = (v: string) => v.replace(/[^0-9]/g, "").length >= 7;
const isUrl = (v: string) => {
  try { new URL(v); return true; } catch { return false; }
};

const arrToInput = (a?: string[] | null) => (a ?? []).join(", ");
const inputToArr = (s: string) => s.split(",").map((x) => x.trim()).filter(Boolean);

const emptyDraft = (): Partial<TeamRow> => ({
  name: "", slug: "", title: "", short_bio: "", bio: "", headshot_url: "",
  phone: "", email: "", license_number: "", license_label: "",
  specialties: [], languages: [], service_areas: [],
  social_links: {}, is_featured: false, is_published: false, sort_order: 0,
});

const AdminTeam = () => {
  const { user } = useAdminAuth();
  const [rows, setRows] = useState<TeamRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pubFilter, setPubFilter] = useState<PubFilter>("all");
  const [editing, setEditing] = useState<Partial<TeamRow> | null>(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Buffer string inputs for arrays
  const [specStr, setSpecStr] = useState("");
  const [langStr, setLangStr] = useState("");
  const [areaStr, setAreaStr] = useState("");

  useEffect(() => {
    document.title = "Team | Hadassah Homes Admin";
    void load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("team_members")
      .select("*")
      .order("sort_order", { ascending: true })
      .order("name", { ascending: true });
    if (err) setError(err.message);
    else setRows((data ?? []) as unknown as TeamRow[]);
    setLoading(false);
  };

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (pubFilter === "published" && !r.is_published) return false;
      if (pubFilter === "draft" && r.is_published) return false;
      return true;
    });
  }, [rows, pubFilter]);

  const openNew = () => {
    setSlugTouched(false);
    setSpecStr(""); setLangStr(""); setAreaStr("");
    setEditing(emptyDraft());
  };
  const openEdit = (row: TeamRow) => {
    setSlugTouched(true);
    setSpecStr(arrToInput(row.specialties));
    setLangStr(arrToInput(row.languages));
    setAreaStr(arrToInput(row.service_areas));
    setEditing(row);
  };

  const togglePublish = async (row: TeamRow) => {
    const { data, error: err } = await supabase
      .from("team_members")
      .update({ is_published: !row.is_published, updated_by: user?.id ?? null } as never)
      .eq("id", row.id)
      .select()
      .single();
    if (err) return toast.error(err.message);
    setRows((prev) => prev.map((r) => (r.id === row.id ? (data as unknown as TeamRow) : r)));
    toast.success(row.is_published ? "Unpublished" : "Published");
  };

  const updateField = <K extends keyof TeamRow>(k: K, v: TeamRow[K]) =>
    setEditing((prev) => (prev ? { ...prev, [k]: v } : prev));

  const updateSocial = (k: keyof SocialLinks, v: string) =>
    setEditing((prev) =>
      prev ? { ...prev, social_links: { ...(prev.social_links ?? {}), [k]: v || undefined } } : prev
    );

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    const name = (editing.name ?? "").trim();
    const slug = (editing.slug ?? "").trim() || slugify(name);
    if (!name) return toast.error("Name is required");
    if (!slug) return toast.error("Slug is required");
    const email = (editing.email ?? "").trim();
    if (email && !isEmail(email)) return toast.error("Invalid email");
    const phone = (editing.phone ?? "").trim();
    if (phone && !isPhone(phone)) return toast.error("Phone looks too short");

    // Validate social URLs if provided
    const social = editing.social_links ?? {};
    for (const key of ["linkedin", "instagram", "facebook", "website"] as (keyof SocialLinks)[]) {
      const v = (social[key] ?? "").trim();
      if (v && !isUrl(v)) return toast.error(`${key} must be a valid URL`);
    }

    setSaving(true);
    const payload: Record<string, unknown> = {
      name,
      slug,
      title: editing.title?.trim() || null,
      short_bio: editing.short_bio?.trim() || null,
      bio: editing.bio?.trim() || null,
      headshot_url: editing.headshot_url?.trim() || null,
      phone: phone || null,
      email: email || null,
      license_number: editing.license_number?.trim() || null,
      license_label: editing.license_label?.trim() || null,
      specialties: inputToArr(specStr),
      languages: inputToArr(langStr),
      service_areas: inputToArr(areaStr),
      social_links: Object.fromEntries(
        Object.entries(social).filter(([, v]) => v && (v as string).trim())
      ),
      is_featured: !!editing.is_featured,
      is_published: !!editing.is_published,
      sort_order: Number(editing.sort_order ?? 0) || 0,
      updated_by: user?.id ?? null,
    };

    if (editing.id) {
      const { data, error: err } = await supabase
        .from("team_members")
        .update(payload as never)
        .eq("id", editing.id)
        .select()
        .single();
      setSaving(false);
      if (err) return toast.error(err.message);
      setRows((prev) => prev.map((r) => (r.id === editing.id ? (data as unknown as TeamRow) : r)));
      toast.success("Saved");
    } else {
      const { data, error: err } = await supabase
        .from("team_members")
        .insert({ ...payload, created_by: user?.id ?? null } as never)
        .select()
        .single();
      setSaving(false);
      if (err) return toast.error(err.message);
      setRows((prev) => [data as unknown as TeamRow, ...prev]);
      toast.success("Created");
    }
    setEditing(null);
  };

  return (
    <AdminLayout
      title="Team"
      description="Manage team member profiles shown on the public website. Only published members appear publicly."
      publicHref="/team"
      publicLabel="View Team page"
    >
      <div className="mx-auto max-w-7xl space-y-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <select
            value={pubFilter}
            onChange={(e) => setPubFilter(e.target.value as PubFilter)}
            className="h-10 rounded-none border border-input bg-background px-3 text-sm"
          >
            <option value="all">All</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => void load()} disabled={loading} className="rounded-none">
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button variant="gold" onClick={openNew} className="rounded-none">
              <Plus className="mr-2 h-4 w-4" />
              New team member
            </Button>
          </div>
        </div>

        <div className="border border-border bg-card">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="p-6 text-destructive">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">
              No team members yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Member</th>
                    <th className="px-4 py-3 hidden md:table-cell">Title</th>
                    <th className="px-4 py-3">Order</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((r) => (
                    <tr key={r.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {r.headshot_url ? (
                            <img
                              src={r.headshot_url}
                              alt=""
                              className="h-10 w-10 border border-border object-cover"
                            />
                          ) : (
                            <div className="h-10 w-10 border border-border bg-muted" />
                          )}
                          <div>
                            <p className="font-medium text-foreground">{r.name}</p>
                            <p className="text-[11px] text-muted-foreground">{r.slug}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                        {r.title ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{r.sort_order}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${
                            r.is_published
                              ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30"
                              : "bg-muted text-muted-foreground border-border"
                          }`}
                        >
                          {r.is_published ? "Published" : "Draft"}
                        </span>
                        {r.is_featured ? (
                          <span className="ml-1 inline-flex items-center border border-accent/30 bg-accent/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-accent">
                            Featured
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => togglePublish(r)} className="rounded-none">
                            {r.is_published ? "Unpublish" : "Publish"}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openEdit(r)} className="rounded-none">
                            Edit
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          {filtered.length} of {rows.length} team members
        </p>
      </div>

      <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
          {editing ? (
            <form onSubmit={handleSave}>
              <SheetHeader>
                <SheetTitle className="font-headline text-2xl">
                  {editing.id ? "Edit team member" : "New team member"}
                </SheetTitle>
                <SheetDescription>
                  Published members appear on the public Team and About pages.
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-5">
                <Field label="Name *">
                  <Input
                    required
                    value={editing.name ?? ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEditing((prev) =>
                        prev ? { ...prev, name: v, slug: slugTouched ? prev.slug : slugify(v) } : prev
                      );
                    }}
                    className="rounded-none"
                  />
                </Field>

                <Field label="Slug *">
                  <Input
                    required
                    value={editing.slug ?? ""}
                    onChange={(e) => { setSlugTouched(true); updateField("slug", slugify(e.target.value)); }}
                    className="rounded-none"
                  />
                </Field>

                <Field label="Title">
                  <Input
                    value={editing.title ?? ""}
                    onChange={(e) => updateField("title", e.target.value)}
                    placeholder="Realtor & Interior Designer"
                    className="rounded-none"
                  />
                </Field>

                <Field label="Headshot">
                  <div className="space-y-2">
                    {editing.headshot_url ? (
                      <div className="flex items-center gap-3">
                        <img
                          src={editing.headshot_url}
                          alt=""
                          className="h-20 w-20 border border-border object-cover"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => updateField("headshot_url", "" as never)}
                          className="rounded-none"
                        >
                          <X className="mr-1 h-3 w-3" /> Remove
                        </Button>
                      </div>
                    ) : null}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setPickerOpen(true)}
                      className="rounded-none"
                    >
                      <ImageIcon className="mr-2 h-4 w-4" />
                      Choose from Media Library
                    </Button>
                    <Input
                      value={editing.headshot_url ?? ""}
                      onChange={(e) => updateField("headshot_url", e.target.value)}
                      placeholder="Or paste a direct image URL"
                      className="rounded-none"
                    />
                  </div>
                </Field>

                <Field label="Short bio">
                  <Textarea
                    rows={2}
                    value={editing.short_bio ?? ""}
                    onChange={(e) => updateField("short_bio", e.target.value)}
                    className="rounded-none"
                  />
                </Field>

                <Field label="Full bio">
                  <Textarea
                    rows={5}
                    value={editing.bio ?? ""}
                    onChange={(e) => updateField("bio", e.target.value)}
                    className="rounded-none"
                  />
                </Field>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Email">
                    <Input
                      type="email"
                      value={editing.email ?? ""}
                      onChange={(e) => updateField("email", e.target.value)}
                      className="rounded-none"
                    />
                  </Field>
                  <Field label="Phone">
                    <Input
                      value={editing.phone ?? ""}
                      onChange={(e) => updateField("phone", e.target.value)}
                      className="rounded-none"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="License number">
                    <Input
                      value={editing.license_number ?? ""}
                      onChange={(e) => updateField("license_number", e.target.value)}
                      className="rounded-none"
                    />
                  </Field>
                  <Field label="License label">
                    <Input
                      value={editing.license_label ?? ""}
                      onChange={(e) => updateField("license_label", e.target.value)}
                      placeholder="MA Real Estate Broker"
                      className="rounded-none"
                    />
                  </Field>
                </div>

                <Field label="Specialties (comma separated)">
                  <Input
                    value={specStr}
                    onChange={(e) => setSpecStr(e.target.value)}
                    placeholder="Buyer rep, Seller rep, Investment"
                    className="rounded-none"
                  />
                </Field>

                <Field label="Languages (comma separated)">
                  <Input
                    value={langStr}
                    onChange={(e) => setLangStr(e.target.value)}
                    placeholder="English, Spanish"
                    className="rounded-none"
                  />
                </Field>

                <Field label="Service areas (comma separated)">
                  <Input
                    value={areaStr}
                    onChange={(e) => setAreaStr(e.target.value)}
                    placeholder="Massachusetts, Dominican Republic"
                    className="rounded-none"
                  />
                </Field>

                <div className="space-y-3 border border-border bg-muted/20 p-4">
                  <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Social links</p>
                  <Input
                    value={editing.social_links?.linkedin ?? ""}
                    onChange={(e) => updateSocial("linkedin", e.target.value)}
                    placeholder="LinkedIn URL"
                    className="rounded-none"
                  />
                  <Input
                    value={editing.social_links?.instagram ?? ""}
                    onChange={(e) => updateSocial("instagram", e.target.value)}
                    placeholder="Instagram URL"
                    className="rounded-none"
                  />
                  <Input
                    value={editing.social_links?.facebook ?? ""}
                    onChange={(e) => updateSocial("facebook", e.target.value)}
                    placeholder="Facebook URL"
                    className="rounded-none"
                  />
                  <Input
                    value={editing.social_links?.website ?? ""}
                    onChange={(e) => updateSocial("website", e.target.value)}
                    placeholder="Website URL"
                    className="rounded-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Sort order">
                    <Input
                      type="number"
                      value={editing.sort_order ?? 0}
                      onChange={(e) => updateField("sort_order", (Number(e.target.value) || 0) as never)}
                      className="rounded-none"
                    />
                  </Field>
                  <div className="space-y-2 pt-6 text-sm">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!editing.is_published}
                        onChange={(e) => updateField("is_published", e.target.checked as never)}
                      />
                      Published
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!editing.is_featured}
                        onChange={(e) => updateField("is_featured", e.target.checked as never)}
                      />
                      Featured
                    </label>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button type="submit" variant="gold" disabled={saving} className="rounded-none">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditing(null)}
                    className="rounded-none"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </form>
          ) : null}
        </SheetContent>
      </Sheet>

      <MediaPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={(asset) => updateField("headshot_url", asset.public_url as never)}
      />
    </AdminLayout>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="mb-2 block text-xs uppercase tracking-[0.14em] text-muted-foreground">
      {label}
    </label>
    {children}
  </div>
);

export default AdminTeam;
